import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { SharedHeader } from "@/components/SharedHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import FollowersList from "@/components/FollowersList";
import { 
  Camera, User, Mail, Phone, MapPin, Calendar, Shield, 
  Gamepad2, Trophy, Save, ArrowLeft, Edit2, X, Star,
  CalendarDays, Users, Clock, MapPinned, XCircle, CheckCircle, UserPlus, Swords
} from "lucide-react";

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, token, setUser } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const fileInputRef = useRef(null);
  
  const [profile, setProfile] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    location: user?.location || "",
    bio: user?.bio || "",
    gaming_preferences: user?.gaming_preferences || [],
    skill_level: user?.skill_level || "intermediate",
  });

  const [stats, setStats] = useState({
    events_attended: 0,
    events_hosted: 0,
    games_played: 0,
    member_since: user?.created_at || new Date().toISOString(),
    rating: 0,
    total_ratings: 0,
  });

  const [myEvents, setMyEvents] = useState({
    hosted: [],
    attending: [],
  });

  const [myChallenges, setMyChallenges] = useState({
    sent: [],
    received: [],
  });

  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingChallenges, setLoadingChallenges] = useState(false);
  const [showFollowers, setShowFollowers] = useState(false);
  const [followStats, setFollowStats] = useState({ followers: 0, following: 0 });

  const gameOptions = ["Spades", "Whist", "Backgammon", "Checkers", "Chess", "Dominoes"];
  const skillLevels = ["beginner", "intermediate", "advanced", "expert"];

  useEffect(() => {
    fetchUserStats();
    fetchMyEvents();
    fetchFollowStats();
    fetchMyChallenges();
  }, []);

  const fetchUserStats = async () => {
    try {
      const response = await axios.get(`${API}/user/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(prev => ({ ...prev, ...response.data }));
    } catch (err) {
      console.log("Stats endpoint not available");
    }
  };

  const fetchFollowStats = async () => {
    if (!user?.id) return;
    try {
      const [followersRes, followingRes] = await Promise.all([
        axios.get(`${API}/users/${user.id}/followers`),
        axios.get(`${API}/users/${user.id}/following`)
      ]);
      setFollowStats({
        followers: followersRes.data.count || 0,
        following: followingRes.data.count || 0
      });
    } catch (err) {
      console.log("Follow stats not available");
    }
  };

  const fetchMyEvents = async () => {
    setLoadingEvents(true);
    try {
      const [hostedRes, attendingRes] = await Promise.all([
        axios.get(`${API}/events/my-events/hosted`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/events/my-events/registered`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setMyEvents({
        hosted: hostedRes.data.events || [],
        attending: attendingRes.data.events || [],
      });
    } catch (err) {
      console.log("Could not fetch events");
    } finally {
      setLoadingEvents(false);
    }
  };

  const fetchMyChallenges = async () => {
    setLoadingChallenges(true);
    try {
      const [sentRes, receivedRes] = await Promise.all([
        axios.get(`${API}/challenges/sent`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/challenges/received`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setMyChallenges({
        sent: sentRes.data.challenges || [],
        received: receivedRes.data.challenges || [],
      });
    } catch (err) {
      console.log("Could not fetch challenges");
    } finally {
      setLoadingChallenges(false);
    }
  };

  const handleAcceptChallenge = async (challengeId) => {
    try {
      await axios.post(`${API}/challenges/${challengeId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Challenge accepted! Event created.");
      fetchMyChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept challenge");
    }
  };

  const handleDeclineChallenge = async (challengeId) => {
    try {
      await axios.post(`${API}/challenges/${challengeId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Challenge declined");
      fetchMyChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to decline challenge");
    }
  };

  const handleCancelChallenge = async (challengeId) => {
    try {
      await axios.post(`${API}/challenges/${challengeId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Challenge cancelled");
      fetchMyChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel challenge");
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PNG, JPG, or GIF file");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      try {
        await axios.post(`${API}/auth/update-avatar`, 
          { avatar: base64 },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        toast.success("Avatar updated!");
        localStorage.setItem("b2b_avatar", base64);
        if (setUser && user) {
          setUser({ ...user, avatar: base64 });
        }
      } catch (err) {
        toast.error("Failed to update avatar");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully!");
      setIsEditing(false);
      if (setUser) {
        setUser({ ...user, ...profile });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to cancel this event?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Event cancelled");
      fetchMyEvents();
    } catch (err) {
      toast.error("Failed to cancel event");
    }
  };

  const handleCancelRSVP = async (eventId) => {
    if (!window.confirm("Are you sure you want to cancel your RSVP?")) return;
    try {
      await axios.post(`${API}/events/${eventId}/cancel-rsvp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("RSVP cancelled");
      fetchMyEvents();
      fetchUserStats();
    } catch (err) {
      toast.error("Failed to cancel RSVP");
    }
  };

  const toggleGamePreference = (game) => {
    setProfile(prev => ({
      ...prev,
      gaming_preferences: prev.gaming_preferences.includes(game)
        ? prev.gaming_preferences.filter(g => g !== game)
        : [...prev.gaming_preferences, game]
    }));
  };

  const avatar = localStorage.getItem("b2b_avatar") || user?.avatar;

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          className={`w-4 h-4 ${i <= rating ? "text-[#7a6200] fill-[#7a6200]" : "text-[#52525B]"}`}
        />
      );
    }
    return stars;
  };

  const getStatusBadge = (status) => {
    const styles = {
      upcoming: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      active: "bg-green-500/20 text-green-400 border-green-500/30",
      completed: "bg-[#7a6200]/20 text-[#7a6200] border-[#7a6200]/30",
      cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-ubuntu border capitalize ${styles[status] || styles.upcoming}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="profile-page">
      <SharedHeader 
        activeSection={null}
        setActiveSection={() => {}}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-6 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-ubuntu text-sm">Back to Home</span>
          </button>

          {/* Profile Header */}
          <div className="flex flex-col md:flex-row items-start gap-6 mb-8">
            {/* Avatar Section */}
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.gif"
                className="hidden"
                onChange={handleAvatarUpload}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-[#7a6200]/50 hover:border-[#7a6200] transition-colors group"
                data-testid="profile-avatar"
              >
                {avatar ? (
                  <img src={avatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#7a6200]/20 flex items-center justify-center">
                    <span className="text-[#7a6200] font-bebas text-4xl">
                      {profile.full_name?.charAt(0) || profile.email?.charAt(0) || "?"}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera className="w-8 h-8 text-white" />
                </div>
              </button>
            </div>

            {/* Name & Status */}
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-2">
                <h1 className="font-bebas text-4xl tracking-wider text-white">
                  {profile.full_name || "Member"}
                </h1>
                {!isEditing && activeTab === "profile" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="border-[#7a6200]/50 text-[#7a6200] hover:bg-[#7a6200]/10"
                    data-testid="edit-profile-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                )}
              </div>
              <p className="text-[#A1A1AA] font-ubuntu flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {profile.email}
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-3">
                <span className={`px-3 py-1 rounded-full text-xs font-ubuntu ${
                  user?.verification_status === "verified" 
                    ? "bg-green-500/20 text-green-400 border border-green-500/30"
                    : "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                }`}>
                  <Shield className="w-3 h-3 inline mr-1" />
                  {user?.verification_status === "verified" ? "Verified" : "Pending Verification"}
                </span>
                <span className="text-[#52525B] font-ubuntu text-sm flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Member since {new Date(stats.member_since).toLocaleDateString()}
                </span>
                {/* Player Rating */}
                <div className="flex items-center gap-1">
                  {renderStars(stats.rating || 0)}
                  <span className="text-[#52525B] text-xs ml-1">
                    ({stats.total_ratings || 0} ratings)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4 text-center">
                <Trophy className="w-6 h-6 text-[#7a6200] mx-auto mb-2" />
                <p className="font-bebas text-2xl text-white">{stats.events_attended}</p>
                <p className="text-[#52525B] text-xs font-ubuntu">Attended</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4 text-center">
                <Gamepad2 className="w-6 h-6 text-[#7a6200] mx-auto mb-2" />
                <p className="font-bebas text-2xl text-white">{stats.events_hosted}</p>
                <p className="text-[#52525B] text-xs font-ubuntu">Hosted</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4 text-center">
                <Trophy className="w-6 h-6 text-[#7a6200] mx-auto mb-2" />
                <p className="font-bebas text-2xl text-white">{stats.games_played}</p>
                <p className="text-[#52525B] text-xs font-ubuntu">Games</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4 text-center">
                <Star className="w-6 h-6 text-[#7a6200] mx-auto mb-2" />
                <p className="font-bebas text-2xl text-white">{(stats.rating || 0).toFixed(1)}</p>
                <p className="text-[#52525B] text-xs font-ubuntu">Rating</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4 text-center">
                <User className="w-6 h-6 text-[#7a6200] mx-auto mb-2" />
                <p className="font-bebas text-2xl text-white capitalize">{profile.skill_level}</p>
                <p className="text-[#52525B] text-xs font-ubuntu">Skill</p>
              </CardContent>
            </Card>
            {/* Followers Card */}
            <Card 
              className="bg-[#0A0A0A] border-white/10 cursor-pointer hover:border-[#7a6200]/50 transition-colors"
              onClick={() => setShowFollowers(true)}
              data-testid="followers-card"
            >
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 text-[#7a6200] mx-auto mb-2" />
                <p className="font-bebas text-2xl text-white">{followStats.followers}</p>
                <p className="text-[#52525B] text-xs font-ubuntu">Followers</p>
              </CardContent>
            </Card>
            {/* Following Card */}
            <Card 
              className="bg-[#0A0A0A] border-white/10 cursor-pointer hover:border-[#7a6200]/50 transition-colors"
              onClick={() => setShowFollowers(true)}
              data-testid="following-card"
            >
              <CardContent className="p-4 text-center">
                <UserPlus className="w-6 h-6 text-[#7a6200] mx-auto mb-2" />
                <p className="font-bebas text-2xl text-white">{followStats.following}</p>
                <p className="text-[#52525B] text-xs font-ubuntu">Following</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-white/10 overflow-x-auto">
            <button
              onClick={() => setActiveTab("profile")}
              className={`px-4 py-3 font-ubuntu text-sm transition-colors border-b-2 whitespace-nowrap ${
                activeTab === "profile"
                  ? "text-[#7a6200] border-[#7a6200]"
                  : "text-[#A1A1AA] border-transparent hover:text-white"
              }`}
              data-testid="tab-profile"
            >
              Profile Info
            </button>
            <button
              onClick={() => setActiveTab("hosted")}
              className={`px-4 py-3 font-ubuntu text-sm transition-colors border-b-2 whitespace-nowrap ${
                activeTab === "hosted"
                  ? "text-[#7a6200] border-[#7a6200]"
                  : "text-[#A1A1AA] border-transparent hover:text-white"
              }`}
              data-testid="tab-hosted"
            >
              Events Hosted ({myEvents.hosted.length})
            </button>
            <button
              onClick={() => setActiveTab("attending")}
              className={`px-4 py-3 font-ubuntu text-sm transition-colors border-b-2 whitespace-nowrap ${
                activeTab === "attending"
                  ? "text-[#7a6200] border-[#7a6200]"
                  : "text-[#A1A1AA] border-transparent hover:text-white"
              }`}
              data-testid="tab-attending"
            >
              Events Attending ({myEvents.attending.length})
            </button>
            <button
              onClick={() => setActiveTab("challenges")}
              className={`px-4 py-3 font-ubuntu text-sm transition-colors border-b-2 whitespace-nowrap flex items-center gap-2 ${
                activeTab === "challenges"
                  ? "text-[#7a6200] border-[#7a6200]"
                  : "text-[#A1A1AA] border-transparent hover:text-white"
              }`}
              data-testid="tab-challenges"
            >
              <Swords className="w-4 h-4" />
              My Challenges ({myChallenges.sent.length + myChallenges.received.length})
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === "profile" && (
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="font-bebas text-xl tracking-wider text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-[#7a6200]" />
                  PROFILE INFORMATION
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Full Name</label>
                    <Input
                      value={profile.full_name}
                      onChange={(e) => setProfile({...profile, full_name: e.target.value})}
                      disabled={!isEditing}
                      className="input-dark"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Email</label>
                    <Input
                      value={profile.email}
                      disabled
                      className="input-dark opacity-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Phone</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                      <Input
                        value={profile.phone}
                        onChange={(e) => setProfile({...profile, phone: e.target.value})}
                        disabled={!isEditing}
                        className="input-dark pl-10"
                        placeholder="(555) 123-4567"
                        data-testid="profile-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Location</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                      <Input
                        value={profile.location}
                        onChange={(e) => setProfile({...profile, location: e.target.value})}
                        disabled={!isEditing}
                        className="input-dark pl-10"
                        placeholder="Houston, TX"
                        data-testid="profile-location-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Bio</label>
                  <Textarea
                    value={profile.bio}
                    onChange={(e) => setProfile({...profile, bio: e.target.value})}
                    disabled={!isEditing}
                    className="input-dark min-h-[100px]"
                    placeholder="Tell us about yourself and your gaming experience..."
                    data-testid="profile-bio-input"
                  />
                </div>

                {/* Gaming Preferences */}
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-3">Gaming Preferences</label>
                  <div className="flex flex-wrap gap-2">
                    {gameOptions.map((game) => (
                      <button
                        key={game}
                        onClick={() => isEditing && toggleGamePreference(game)}
                        disabled={!isEditing}
                        className={`px-4 py-2 rounded-full text-sm font-ubuntu transition-colors ${
                          profile.gaming_preferences.includes(game)
                            ? "bg-[#7a6200] text-black"
                            : "bg-white/5 text-[#A1A1AA] border border-white/10"
                        } ${isEditing ? "hover:border-[#7a6200]" : "cursor-default"}`}
                        data-testid={`game-pref-${game.toLowerCase()}`}
                      >
                        {game}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Skill Level */}
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-3">Skill Level</label>
                  <div className="flex flex-wrap gap-2">
                    {skillLevels.map((level) => (
                      <button
                        key={level}
                        onClick={() => isEditing && setProfile({...profile, skill_level: level})}
                        disabled={!isEditing}
                        className={`px-4 py-2 rounded text-sm font-ubuntu capitalize transition-colors ${
                          profile.skill_level === level
                            ? "bg-[#7a6200] text-black"
                            : "bg-white/5 text-[#A1A1AA] border border-white/10"
                        } ${isEditing ? "hover:border-[#7a6200]" : "cursor-default"}`}
                        data-testid={`skill-${level}`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                {isEditing && (
                  <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                    <Button
                      variant="outline"
                      onClick={() => setIsEditing(false)}
                      className="border-white/20 text-[#A1A1AA]"
                      data-testid="cancel-edit-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={loading}
                      className="btn-gold"
                      data-testid="save-profile-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Events Hosted Tab */}
          {activeTab === "hosted" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-bebas text-xl tracking-wider text-white">MY HOSTED EVENTS</h2>
                <Button
                  onClick={() => navigate("/home", { state: { section: "host-game" }})}
                  className="btn-gold"
                  data-testid="create-event-btn"
                >
                  Host New Event
                </Button>
              </div>
              
              {loadingEvents ? (
                <div className="text-center py-12 text-[#A1A1AA]">Loading events...</div>
              ) : myEvents.hosted.length === 0 ? (
                <Card className="bg-[#0A0A0A] border-white/10">
                  <CardContent className="py-12 text-center">
                    <Gamepad2 className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                    <p className="text-[#A1A1AA] font-ubuntu">You have not hosted any events yet.</p>
                    <Button
                      onClick={() => navigate("/home", { state: { section: "host-game" }})}
                      className="btn-outline-gold mt-4"
                    >
                      Host Your First Event
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {myEvents.hosted.map((event) => (
                    <Card key={event.id} className="bg-[#0A0A0A] border-white/10 hover:border-[#7a6200]/30 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-bebas text-lg text-white">{event.title}</h3>
                              {getStatusBadge(event.status)}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-[#A1A1AA]">
                              <span className="flex items-center gap-1">
                                <CalendarDays className="w-4 h-4" />
                                {event.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {event.time}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {event.current_participants}/{event.max_participants}
                              </span>
                              <span className="flex items-center gap-1">
                                <Gamepad2 className="w-4 h-4" />
                                {event.game_type}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/event/${event.id}`)}
                              className="border-white/20 text-[#A1A1AA]"
                            >
                              View
                            </Button>
                            {event.status !== "cancelled" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCancelEvent(event.id)}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                data-testid={`cancel-event-${event.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Events Attending Tab */}
          {activeTab === "attending" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-bebas text-xl tracking-wider text-white">EVENTS I AM ATTENDING</h2>
                <Button
                  onClick={() => navigate("/home", { state: { section: "find-games" }})}
                  className="btn-outline-gold"
                >
                  Find More Events
                </Button>
              </div>
              
              {loadingEvents ? (
                <div className="text-center py-12 text-[#A1A1AA]">Loading events...</div>
              ) : myEvents.attending.length === 0 ? (
                <Card className="bg-[#0A0A0A] border-white/10">
                  <CardContent className="py-12 text-center">
                    <CalendarDays className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                    <p className="text-[#A1A1AA] font-ubuntu">You have not registered for any events yet.</p>
                    <Button
                      onClick={() => navigate("/home", { state: { section: "find-games" }})}
                      className="btn-outline-gold mt-4"
                    >
                      Browse Events
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {myEvents.attending.map((event) => (
                    <Card key={event.id} className="bg-[#0A0A0A] border-white/10 hover:border-[#7a6200]/30 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-bebas text-lg text-white">{event.title}</h3>
                              {getStatusBadge(event.status)}
                              <CheckCircle className="w-4 h-4 text-green-400" title="RSVP Confirmed" />
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-[#A1A1AA]">
                              <span className="flex items-center gap-1">
                                <CalendarDays className="w-4 h-4" />
                                {event.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {event.time}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPinned className="w-4 h-4" />
                                {event.location}
                              </span>
                              <span className="flex items-center gap-1">
                                <Gamepad2 className="w-4 h-4" />
                                {event.game_type}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/event/${event.id}`)}
                              className="border-white/20 text-[#A1A1AA]"
                            >
                              View
                            </Button>
                            {event.status === "upcoming" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCancelRSVP(event.id)}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                data-testid={`cancel-rsvp-${event.id}`}
                              >
                                Cancel RSVP
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Challenges Tab */}
          {activeTab === "challenges" && (
            <div className="space-y-6">
              {/* Received Challenges */}
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="font-bebas text-xl tracking-wider text-white flex items-center gap-2">
                    <Swords className="w-5 h-5 text-[#7a6200]" />
                    CHALLENGES RECEIVED ({myChallenges.received.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingChallenges ? (
                    <div className="text-center py-8">
                      <div className="w-6 h-6 border-2 border-[#7a6200] border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </div>
                  ) : myChallenges.received.length === 0 ? (
                    <div className="text-center py-8 text-[#52525B]">
                      <Swords className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="font-ubuntu">No challenges received</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {myChallenges.received.map((challenge) => (
                        <div 
                          key={challenge.id} 
                          className={`p-4 rounded-lg border ${
                            challenge.status === "pending" 
                              ? "bg-[#7a6200]/10 border-[#7a6200]/30" 
                              : "bg-white/5 border-white/10"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-full overflow-hidden border border-white/20">
                                {challenge.challenger_avatar ? (
                                  <img src={challenge.challenger_avatar} alt={challenge.challenger_name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full bg-[#7a6200]/20 flex items-center justify-center">
                                    <span className="text-[#7a6200] font-bebas">{challenge.challenger_name?.charAt(0)}</span>
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="text-white font-ubuntu font-medium">{challenge.challenger_name}</p>
                                <p className="text-[#7a6200] font-ubuntu text-sm">{challenge.game_type}</p>
                              </div>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-ubuntu capitalize ${
                              challenge.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                              challenge.status === "accepted" ? "bg-green-500/20 text-green-400" :
                              challenge.status === "declined" ? "bg-red-500/20 text-red-400" :
                              "bg-[#52525B]/20 text-[#52525B]"
                            }`}>
                              {challenge.status}
                            </span>
                          </div>
                          
                          <div className="mt-3 flex items-center gap-4 text-[#A1A1AA] text-sm font-ubuntu">
                            <span className="flex items-center gap-1">
                              <CalendarDays className="w-4 h-4" />
                              {new Date(challenge.proposed_date).toLocaleDateString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {challenge.proposed_time}
                            </span>
                            {challenge.location && (
                              <span className="flex items-center gap-1">
                                <MapPinned className="w-4 h-4" />
                                {challenge.location}
                              </span>
                            )}
                          </div>

                          {challenge.message && (
                            <p className="mt-2 text-[#52525B] text-sm font-ubuntu italic">&quot;{challenge.message}&quot;</p>
                          )}

                          {challenge.stakes && (
                            <p className="mt-1 text-[#C6A300] text-sm font-ubuntu">
                              <Trophy className="w-3 h-3 inline mr-1" />
                              Stakes: {challenge.stakes}
                            </p>
                          )}

                          {challenge.status === "pending" && (
                            <div className="mt-4 flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleAcceptChallenge(challenge.id)}
                                className="btn-gold flex-1"
                                data-testid={`accept-challenge-${challenge.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeclineChallenge(challenge.id)}
                                className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                                data-testid={`decline-challenge-${challenge.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Decline
                              </Button>
                            </div>
                          )}

                          {challenge.status === "accepted" && challenge.event_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/events/${challenge.event_id}`)}
                              className="mt-4 w-full border-[#7a6200]/50 text-[#7a6200]"
                            >
                              View Match Event
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sent Challenges */}
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="font-bebas text-xl tracking-wider text-white flex items-center gap-2">
                    <Swords className="w-5 h-5 text-[#7a6200]" />
                    CHALLENGES SENT ({myChallenges.sent.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingChallenges ? (
                    <div className="text-center py-8">
                      <div className="w-6 h-6 border-2 border-[#7a6200] border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </div>
                  ) : myChallenges.sent.length === 0 ? (
                    <div className="text-center py-8 text-[#52525B]">
                      <Swords className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="font-ubuntu">No challenges sent</p>
                      <Button 
                        onClick={() => navigate("/discover")}
                        className="btn-gold mt-4"
                      >
                        Find Players to Challenge
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {myChallenges.sent.map((challenge) => (
                        <div 
                          key={challenge.id} 
                          className={`p-4 rounded-lg border ${
                            challenge.status === "pending" 
                              ? "bg-blue-500/10 border-blue-500/30" 
                              : "bg-white/5 border-white/10"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-full overflow-hidden border border-white/20">
                                {challenge.opponent_avatar ? (
                                  <img src={challenge.opponent_avatar} alt={challenge.opponent_name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full bg-[#7a6200]/20 flex items-center justify-center">
                                    <span className="text-[#7a6200] font-bebas">{challenge.opponent_name?.charAt(0)}</span>
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="text-white font-ubuntu font-medium">{challenge.opponent_name}</p>
                                <p className="text-[#7a6200] font-ubuntu text-sm">{challenge.game_type}</p>
                              </div>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-ubuntu capitalize ${
                              challenge.status === "pending" ? "bg-blue-500/20 text-blue-400" :
                              challenge.status === "accepted" ? "bg-green-500/20 text-green-400" :
                              challenge.status === "declined" ? "bg-red-500/20 text-red-400" :
                              challenge.status === "cancelled" ? "bg-[#52525B]/20 text-[#52525B]" :
                              "bg-[#52525B]/20 text-[#52525B]"
                            }`}>
                              {challenge.status}
                            </span>
                          </div>
                          
                          <div className="mt-3 flex items-center gap-4 text-[#A1A1AA] text-sm font-ubuntu">
                            <span className="flex items-center gap-1">
                              <CalendarDays className="w-4 h-4" />
                              {new Date(challenge.proposed_date).toLocaleDateString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {challenge.proposed_time}
                            </span>
                            {challenge.location && (
                              <span className="flex items-center gap-1">
                                <MapPinned className="w-4 h-4" />
                                {challenge.location}
                              </span>
                            )}
                          </div>

                          {challenge.message && (
                            <p className="mt-2 text-[#52525B] text-sm font-ubuntu italic">&quot;{challenge.message}&quot;</p>
                          )}

                          {challenge.stakes && (
                            <p className="mt-1 text-[#C6A300] text-sm font-ubuntu">
                              <Trophy className="w-3 h-3 inline mr-1" />
                              Stakes: {challenge.stakes}
                            </p>
                          )}

                          {challenge.status === "pending" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCancelChallenge(challenge.id)}
                              className="mt-4 w-full border-[#52525B]/50 text-[#52525B] hover:bg-[#52525B]/10"
                              data-testid={`cancel-challenge-${challenge.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Cancel Challenge
                            </Button>
                          )}

                          {challenge.status === "accepted" && challenge.event_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/events/${challenge.event_id}`)}
                              className="mt-4 w-full border-[#7a6200]/50 text-[#7a6200]"
                            >
                              View Match Event
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-white/10 bg-[#0A0A0A] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#52525B] font-ubuntu text-sm">
            © 2026 All Rights Reserved | Board2Boston™ | 713 Consulting and Development
          </p>
          <p className="text-[#52525B] font-ubuntu text-xs mt-2">
            1942 W. Gray St., Box #1259 | Houston, TX 77019 | United States
          </p>
        </div>
      </footer>

      {/* Followers/Following Modal */}
      <FollowersList
        userId={user?.id}
        token={token}
        currentUserId={user?.id}
        isOpen={showFollowers}
        onClose={() => {
          setShowFollowers(false);
          fetchFollowStats(); // Refresh stats when modal closes
        }}
      />
    </div>
  );
};

export default ProfilePage;
